import { QueryType } from '~api/features/Default';
import {
    IProductDataOptionsFilters,
    IProductDataOptionsSkuFilter,
    IProductDataOptionsSkusSorter,
    IProductDataOptionsSorters,
    IProductDataOptionsTagFilter,
    IProductDataOptionsTagSorter,
    SortDirection
} from '~api/interfaces/IProductDataOptions';
import { IQueryTypeFragments } from '~api/QueryFragments';
import { sanitizeJsonForQuery } from '~utils/ContentFormatter';

const buildSkuFilter = (filters: IProductDataOptionsSkuFilter) => {
    const { skuIds, includeInnerHits, shades, isDefaultSku, inventoryStatus } = filters;

    return {
        skus: {
            filter: {
                ...(skuIds && { sku_id: { in: skuIds } }),
                ...(inventoryStatus && { inventory_status: { in: inventoryStatus } }),
                ...(isDefaultSku && { is_default_sku: { eq: isDefaultSku } }),
                ...(shades && { shades: { name: { in: shades } } })
            },
            ...(typeof includeInnerHits !== 'undefined' && { includeInnerHits })
        }
    };
};

const buildProductFilter = (productIds: string[]) => ({ product_id: { in: productIds } });

const buildTagFilter = (filters: IProductDataOptionsTagFilter) => {
    const { ids, keys, values, includeInnerHits } = filters;

    return {
        tags: {
            filter: {
                ...(ids && { id: { in: ids } }),
                ...(keys && { key: { in: keys } }),
                ...(values && { value: { in: values } })
            },
            ...(typeof includeInnerHits !== 'undefined' && { includeInnerHits })
        }
    };
};

export const buildQueryFilters = (filters: IProductDataOptionsFilters) => {
    const { tags, skus, productIds } = filters;

    const queryFilters = [];

    skus && queryFilters.push(buildSkuFilter(skus));
    productIds && queryFilters.push(buildProductFilter(productIds));
    tags && queryFilters.push(buildTagFilter(tags));

    return sanitizeJsonForQuery(JSON.stringify(queryFilters));
};

const buildTagSorter = (sorterOptions: IProductDataOptionsTagSorter) => ({
    tags: { product_display_order: sorterOptions.productDisplayOrder }
});

const buildProductIdSorter = (sorterOptions: SortDirection) => ({ product_id: sorterOptions });

const buildSkusSorter = (sorterOptions: IProductDataOptionsSkusSorter) => {
    const { shades, skuIds } = sorterOptions;

    return {
        skus: {
            ...(skuIds && { sku_id: skuIds }),
            ...(shades && { shades: { name: shades.name } })
        }
    };
};

const sanitizeJsonForSorter = (jsonString: string) => {
    const sanitizedSorter = sanitizeJsonForQuery(jsonString);

    const directions = Object.values(SortDirection);

    const sanitizeEnumValues = (sorter: string, direction: SortDirection) => {
        const regex = new RegExp(`"${direction}"`, 'g');

        return sorter.replace(regex, direction);
    };

    return directions.reduce(sanitizeEnumValues, sanitizedSorter);
};

export const buildQuerySorters = (sorters: IProductDataOptionsSorters) => {
    const { tags, skus, productIds } = sorters;

    const querySorters = [];

    tags && querySorters.push(buildTagSorter(tags));
    productIds && querySorters.push(buildProductIdSorter(productIds));
    skus && querySorters.push(buildSkusSorter(skus));

    return sanitizeJsonForSorter(JSON.stringify(querySorters));
};

export const buildSelectionSet = (customQuery: string, queryFragments: IQueryTypeFragments) => {
    const query =
        customQuery ||
        `
        items {
            ${queryFragments[QueryType.product]}
            skus {
                total
                items {
                    ${queryFragments[QueryType.skus]}
                }
            }
        }
    `;

    return `
        ${queryFragments[QueryType.collection]}
        ${query}
    `;
};
